import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Facebook,
  Twitter,
  Instagram
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import RotatingText from '../components/common/RotatingText';
import GolfClubsMap from '../components/map/GolfClubsMap';
import { fetchGolfClubs } from '../services/clubsApi';

const ContactPage = () => {
  const { isDarkMode } = useTheme();
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load clubs from API
  useEffect(() => {
    const loadClubs = async () => {
      try {
        setLoading(true);
        const data = await fetchGolfClubs({ withCoordinates: true });
        setClubs(data);
      } catch (err) {
        console.error('Error loading clubs:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadClubs();
  }, []);

  const handleClubSelect = (club) => {
    setSelectedClub(club);
  };

  // Contact information
  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      primary: 'operations@fsgu.co.za',
      secondary: 'General inquiries',
      link: 'mailto:operations@fsgu.co.za'
    },
    {
      icon: Phone,
      title: 'Phone',
      primary: '057 899 1724',
      secondary: 'Mon - Fri, 8:00 AM - 5:00 PM',
      link: 'tel:+27578991724'
    },
    {
      icon: MapPin,
      title: 'Postal Address',
      primary: 'P.O. Box 124',
      secondary: 'Wesselsbron, 9680',
      link: null
    },
    {
      icon: Clock,
      title: 'Office Hours',
      primary: 'Monday - Friday',
      secondary: '8:00 AM - 5:00 PM',
      link: null
    }
  ];

  // Department contacts
  const departments = [
    {
      name: 'Tournament Enquiries',
      email: 'operations@fsgu.co.za',
      description: 'Questions about upcoming tournaments, entries, and results'
    },
    
    {
      name: 'General Information',
      email: 'operations@fsgu.co.za',
      description: 'All other questions and feedback'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/contactHero.png" 
            alt="Contact Us" 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  Contact Us
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Get in touch</span>
              <RotatingText
                texts={[
                  'Today',
                  'Anytime',
                  'Easily',
                  'Now',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Connect with Free State Golf Union
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Affiliated Golf Clubs
            </h2>

            <p className={`text-lg ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              {clubs.length > 0 ? `Explore ${clubs.length} golf clubs` : 'Loading clubs'} across the Free State province
            </p>
          </div>

          {/* Map Component */}
          <div className={`rounded-2xl overflow-hidden border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-silver-50 border-fsgu-silver-200'
          }`}>
            {loading ? (
              <div className="aspect-video flex items-center justify-center">
                <div className="text-center">
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-fsgu-green-400 to-fsgu-green-600' 
                      : 'bg-gradient-to-br from-fsgu-green-500 to-fsgu-green-700'
                  }`}>
                    <MapPin className="w-6 h-6 text-fsgu-white animate-pulse" />
                  </div>
                  <p className={`text-sm font-medium ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-500'
                  }`}>
                    Loading golf clubs...
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="aspect-video flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                  <MapPin className={`w-12 h-12 mx-auto mb-4 ${
                    isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-500'
                  }`} />
                  <p className={`text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'
                  }`}>
                    Unable to load map
                  </p>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                  }`}>
                    {error}
                  </p>
                </div>
              </div>
            ) : (
              <div className="aspect-video">
                <GolfClubsMap
                  clubs={clubs}
                  selectedClub={selectedClub}
                  onClubSelect={handleClubSelect}
                  className="h-full w-full"
                />
              </div>
            )}
          </div>

          {/* Selected Club Info */}
          {selectedClub && (
            <div className={`mt-6 p-6 rounded-2xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <h3 className={`text-xl font-bold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                {selectedClub.name}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedClub.address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {selectedClub.address}
                      </p>
                      {selectedClub.city && (
                        <p className={`text-sm ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          {selectedClub.city}, {selectedClub.postal_code}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {selectedClub.phone && (
                  <div className="flex items-start space-x-3">
                    <Phone className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <a
                      href={`tel:${selectedClub.phone}`}
                      className={`text-sm hover:underline ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}
                    >
                      {selectedClub.phone}
                    </a>
                  </div>
                )}

                {selectedClub.email && (
                  <div className="flex items-start space-x-3">
                    <Mail className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <a
                      href={`mailto:${selectedClub.email}`}
                      className={`text-sm hover:underline ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}
                    >
                      {selectedClub.email}
                    </a>
                  </div>
                )}

                {selectedClub.holes && (
                  <div className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    ⛳ {selectedClub.holes} Holes
                    {selectedClub.par && ` • Par ${selectedClub.par}`}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className={`section ${
        isDarkMode ? 'bg-fsgu-gray-800/50' : 'bg-fsgu-silver-50'
      }`}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Get In Touch
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              const content = (
                <div className="card-hover h-full text-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                    isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                  }`}>
                    <IconComponent className={`w-6 h-6 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                  </div>

                  <h3 className={`text-sm font-medium mb-2 uppercase tracking-wider ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    {info.title}
                  </h3>

                  <p className={`text-lg font-semibold mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {info.primary}
                  </p>

                  <p className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                  }`}>
                    {info.secondary}
                  </p>
                </div>
              );

              return info.link ? (
                <a
                  key={index}
                  href={info.link}
                  className="block transition-transform duration-300 hover:scale-105"
                >
                  {content}
                </a>
              ) : (
                <div key={index}>
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Department Contacts */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Contact Departments
              </h2>

              <p className={`text-lg ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Reach out to the right department for faster assistance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {departments.map((dept, index) => (
                <a
                  key={index}
                  href={`mailto:${dept.email}`}
                  className="card-hover group"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-fsgu-green-500/20 group-hover:bg-fsgu-green-500/30' 
                        : 'bg-fsgu-green-500/10 group-hover:bg-fsgu-green-500/20'
                    }`}>
                      <Mail className={`w-6 h-6 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                      }`}>
                        {dept.name}
                      </h3>

                      <p className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                        isDarkMode 
                          ? 'text-fsgu-green-400 group-hover:text-fsgu-green-300' 
                          : 'text-fsgu-green-500 group-hover:text-fsgu-green-600'
                      }`}>
                        {dept.email}
                      </p>

                      <p className={`text-sm ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        {dept.description}
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Social Media */}
            <div className={`p-8 rounded-2xl border text-center ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Follow Us on Social Media
              </h3>

              <p className={`text-sm mb-6 ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Stay updated with the latest news, tournament results, and announcements
              </p>

              <div className="flex items-center justify-center gap-3">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-lg transition-all duration-300 hover:scale-110 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 hover:bg-fsgu-green-500/20 text-fsgu-silver-400 hover:text-fsgu-green-400'
                      : 'bg-fsgu-silver-100 hover:bg-fsgu-green-500/10 text-fsgu-silver-600 hover:text-fsgu-green-500'
                  }`}
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-lg transition-all duration-300 hover:scale-110 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 hover:bg-fsgu-green-500/20 text-fsgu-silver-400 hover:text-fsgu-green-400'
                      : 'bg-fsgu-silver-100 hover:bg-fsgu-green-500/10 text-fsgu-silver-600 hover:text-fsgu-green-500'
                  }`}
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-lg transition-all duration-300 hover:scale-110 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 hover:bg-fsgu-green-500/20 text-fsgu-silver-400 hover:text-fsgu-green-400'
                      : 'bg-fsgu-silver-100 hover:bg-fsgu-green-500/10 text-fsgu-silver-600 hover:text-fsgu-green-500'
                  }`}
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;